
import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import {
  ItemSet,
  Section,
  IDocumentAlert,
} from "../../../../domain/document";
import { PatientProfileInformation } from "../../../../domain/patient";
import {
  TypeKeys,
  IItem,
  IDrawableItem, instanceOfIDrawable,
  MovableImageItem,
  PrintBehavior,
  instanceOfIRelayoutable,
  RichTextBoxItem,
  instanceOfIRuleInjectable,
} from "../../../../domain/document/items";
import { EditingDocument } from "..";
import { IConfiguration } from "../../../../domain/configuration";
import { UpdateObjectCountExceedError } from "../../../../domain/system/error";
import { InstitutionItem } from "../../../../domain/institution";

@Component({
})
export default class DocumentStructureEditableMixin extends Mixins() {
  editingSource: EditingDocument;
  writePropertyWhenValid: (writer: (source: EditingDocument) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: EditingDocument) => T, defaultValue: T) => T;

  get isEditingDocumentAlreadyInitialized(): boolean {
    return this.readPropertyOrDefault(s => s.isAlreadyInitialized, false);
  }

  get itemSets(): ItemSet[] {
    return this.readPropertyOrDefault(s => s.itemSets, []);
  }

  get sections(): Section[] {
    return this.readPropertyOrDefault(s => s.sections, []);
  }

  get currentSectionIndex(): number {
    return this.readPropertyOrDefault(s => s.currentSectionIndex, -1);
  }

  set currentSectionIndex(value: number) {
    this.writePropertyWhenValid(s => s.currentSectionIndex = value);
  }

  get currentSection(): Section {
    return this.readPropertyOrDefault(s => s.currentSection, null);
  }

  get itemSetId(): number {
    return this.readPropertyOrDefault(s => s.itemSetId, -1);
  }

  get itemSet(): ItemSet {
    return this.readPropertyOrDefault(s => s.itemSet, null);
  }

  get items(): IItem[] {
    return this.readPropertyOrDefault(s => s.items, []);
  }

  get currentSectionItems(): IItem[] {
    return !this.currentSection
      ? []
      : Enumerable.from(this.currentSection.itemPrivateIds)
        .select(i => this.items[i])
        .toArray();
  }

  get drawableItemsOnPage(): IDrawableItem[] {
    if (!this.currentSection) {
      return [];
    }
    // V1から移行した文書の自由描画はページいっぱいの画像になっているので対策している
    const freeDrawnItems: IDrawableItem[] = [];
    const normalItems: IDrawableItem[] = [];
    for (const drawableItem of this.drawableItems) {
      if (drawableItem instanceof MovableImageItem && drawableItem.isFreeDrawn) {
        freeDrawnItems.push(drawableItem);
      } else {
        // 初期化完了前にリッチテキストオブジェクト側から更新通知が来ると初期化が終わらなくなるケースがあるので対策している
        if (this.isEditingDocumentAlreadyInitialized || !(drawableItem instanceof RichTextBoxItem)) {
          normalItems.push(drawableItem);
        }
      }
    }
    const currentSectionItems = freeDrawnItems.concat(normalItems);
    return this.shouldHideOutsidePage
      ? this.extractSectionOverlappingItems(currentSectionItems)
      : currentSectionItems;
  }

  /**
   * 枠外のオブジェクトを除去する処理
   * 可変したときにページ下部のオブジェクトが消えてしまわないよう、ページ内に可変オブジェクトが有るかどうかで対応を変えている
   * 有るとき：bodyRectangleに重なる部分があるオブジェクトを抽出（縦方向はチェックしない）
   * 無いとき：bodyRectangleに重なる部分があるオブジェクトを抽出
   */
  private extractSectionOverlappingItems(items: IDrawableItem[]): IDrawableItem[] {
    const isRelayoutableByObjectHeight = Enumerable.from(items).any(i => instanceOfIRelayoutable(i) && i.isRelayoutableByHeight);
    return isRelayoutableByObjectHeight
      ? Enumerable.from(items).where(i => this.currentSection.bodyRectangle.overlapsHorizontal(i.geometry.rectangle)).toArray()
      : Enumerable.from(items).where(i => this.currentSection.bodyRectangle.overlaps(i.geometry.rectangle)).toArray();
  }
  
  get shouldHideOutsidePage(): boolean {
    return this.$configuration.behavior.document.editor.objects.shouldHideOutsidePage;
  }

  get freeDrawnItemsOnPage(): IDrawableItem[] {
    if (!this.currentSection) {
      return [];
    }
    // V1から移行した文書の自由描画の最前面の表示用
    const freeDrawnItems: IDrawableItem[] = [];
    for (const drawableItem of this.drawableItems) {
      if (drawableItem instanceof MovableImageItem && drawableItem.isFreeDrawn) {
        freeDrawnItems.push(drawableItem);
      }
    }
    return freeDrawnItems;
  }

  get drawableItems(): IDrawableItem[] {
    return this.readPropertyOrDefault(s => s.drawableItems, []);
  }

  get lastFocusedItem(): IItem {
    return  this.readPropertyOrDefault(s => s.lastFocusedItem, null);
  }

  set lastFocusedItem(value: IItem) {
    this.writePropertyWhenValid(s => s.lastFocusedItem = value);
  }

  get patientProfile(): PatientProfileInformation {
    return this.readPropertyOrDefault(s => s.patientProfile, null);
  }

  set patientProfile(value: PatientProfileInformation) {
    this.writePropertyWhenValid(s => s.patientProfile = value);
  }

  get selectedInstitution(): InstitutionItem {
    return this.readPropertyOrDefault(s => s.selectedInstitution, null);
  }

  set selectedInstitution(value: InstitutionItem) {
    this.writePropertyWhenValid(s => s.selectedInstitution = value);
  }

  get alerts(): IDocumentAlert[] {
    return this.readPropertyOrDefault(s => s.alerts, []);
  }

  get shouldMarkAlerts(): boolean {
    return this.readPropertyOrDefault(s => s.shouldMarkAlerts, false);
  }

  /**
   * asyncなので呼び出し元の対応が必要
   */
  executeStackedScripts(): void {
    if (!this.editingSource || !this.editingSource.externalScriptRunner.hasRequestedScripts) { return; }
    this.executeStackedScriptsAsync()
    .then(() => {
      // this.$success("成功", "外部スクリプトの実行が成功しました。");
    }).catch(e => {
      this.$error("エラー", "外部スクリプトの実行でエラーが発生しました。", e);
    });
  }

  async executeStackedScriptsAsync(): Promise<void> {
    if (!this.editingSource) {
      return;
    }
    const isSuccess = await this.editingSource.executeStackedScriptsAsyncWithResult();
    if (!isSuccess) {
      throw new Error("外部スクリプトの実行中にエラーが発生しています。");
    }
  }

  addItem(added: IItem, sectionIndex?: number): void {
    this.writePropertyWhenValid(s => s.addItem(added, sectionIndex));
    // this.executeStackedScripts(updater);
  }

  isItemRemovable(source: IItem): boolean {
    return source
      && (source.isAdditional || (source.typeKey === TypeKeys.MovableTextBoxItem) || (source.typeKey === TypeKeys.MovableImageItem))
      && !this.isEditDisabledByInjectedRule(source);
  }

  private isEditDisabledByInjectedRule(source: IItem): boolean {
    return instanceOfIRuleInjectable(source) && source.injectedRule && source.injectedRule.isEditDisabled;
  }

  removeItem(removed: IItem): void {
    if (!this.isItemRemovable(removed)) {
      return;
    }
    if (removed.isAdditional) {
      this._removeItem(removed);
    } else {  // フォーマット内貼り付け画像用.(v1同様削除できるように.)
      const item = removed.clone();
      item.isDeleted = true;
      if (instanceOfIDrawable(item)) {
        item.isVisible = false;
        item.printBehavior = new PrintBehavior(false, false);
      }
      this.updateItem(item);
    }
  }

  _removeItem(removed: IItem, sectionIndex?: number): void {
    this.writePropertyWhenValid(s => s.removeItem(removed, sectionIndex));
    // this.executeStackedScripts(updater);
  }

  updateItem(updated: IItem, itemSetId?: number): void {
    const configuration = this.$store.getters.configuration as IConfiguration;
    this.writePropertyWhenValid(s => {
      try {
        s.updateItem(updated, {
          itemSetId,
        });
      } catch (e) {
        if (e instanceof UpdateObjectCountExceedError) {
          const level = configuration.behavior.document.editor.chainedObjectUpdation.overflow.level;
          if (level === "warning") {
            this.$warn("警告", e.message);
            return;
          }
        }
        this.$error("エラー", e.message, e);
      }
    });
    const intervalMilliSeconds = configuration.behavior.document.editor.externalScript.executeIntervalMilliSeconds;
    // タイマー実行する場合はDocumentPageEditorで定期実行している
    if (intervalMilliSeconds <= 0) {
      // editingSourceの更新が反映されてから実行.
      this.$nextTick(() => {
        this.executeStackedScripts();
      });
    }
  }
}
